/* cspell:ignore nics */
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import {
  applyRegistryToGroups,
  mergeRegistryPayload,
  placeRegistryOnGroupSystem,
  readGroupSystemRegistry,
  readRegistryPayloadFromValues,
} from '@/delivery-type-plugin/capabilities/image';
import { getUserInfo } from '@/utils/useLocalStorage';

import { initCommandFormFieldsToApi } from '../../../capabilities/command-args-normal/utils';
import { mergeImageStorageVolumesIntoSubmitValues } from '../../../capabilities/maas-image-storage/imageStorageVolumes';
import type { MaasModelSource, MaasSubmitError } from '../../../capabilities/maas-init/maasTypes';
import { MaasGroupRoleType, MaasMode, MaasRunMode, normalizeMaasRuntime } from '../../../capabilities/maas-init/maasTypes';
import { buildModelInstanceMaasFormAnnotations } from '../../../capabilities/maas-init/utils/annotations';
import { resolveCommonEnvFromValues } from '../../../capabilities/maas-init/utils/commonEnv';
import { parseFormJsonField } from '../../../capabilities/maas-init/utils/jsonFormField';
import { normalizeModelCategory } from '../../../capabilities/maas-model-storage/modelCategory';
import { readModelInstanceFormValues, resolveModelInstancePdSeparation } from '../../../capabilities/maas-model-storage/modelFormUtils';
import type {
  MaasModel,
  MaasModelSourceEntry,
  ModelInstanceFormValues,
  ModelInstanceSpec,
} from '../../../capabilities/maas-model-storage/modelInstanceTypes';
import { buildExistingPvcModelSource } from '../../../capabilities/maas-model-storage/modelSourcePlaceholder';
import { MODEL_DATA_VOLUME_NAME, isExistingDataVolume } from '../../../capabilities/maas-model-storage/storageVolume';
import {
  attachPrimaryDataVolumeForSubmit,
  getPrimaryGroupFromValues,
  resolvePrimaryDataVolumeFromValues,
} from '../../../capabilities/maas-node-groups/masterGroupForm';
import { GATEWAY_NODES_FIELD } from '../../../capabilities/maas-node-groups/nodeFieldNames';
import {
  type EmbeddedNodeSpec,
  assembleGroupSpecsFromNodeFields,
  mergeEmbeddedNodeForSubmit,
  parseNodesField,
} from '../../../capabilities/maas-node-groups/nodeFields';
import {
  ensureFrameworkForSubmit,
  normalizeMaasGroupResourceForApi,
  normalizeMaasGroupSpec,
  resolveFrameworkFromInferenceImage,
  resolveGroupSpecsForSubmit,
} from '../../../capabilities/maas-node-groups/payload';
import { resolveApiRunModeForSubmit } from '../../../capabilities/maas-node-groups/submitValidation';
import {
  resolveContainerDeployConfigFromFormValues,
  stripContainerDeployConfigFromGroupedSpec,
} from '../../../capabilities/maas-system-config/deployConfigPayload';
import { parseTaskTemplateField } from '../../../capabilities/maas-task-template/utils';
import { serializeServiceListForApi } from '../../../capabilities/service/serviceApiField';
import { isMiGatewayModuleEnabled } from '../gatewayModule';
import { SERVICE_MOUNT_GROUP_NAME_FIELD, normalizeServiceMountGroupNames } from '../serviceMountNode';
import { confirmModelInstanceImageOnForm } from './imageConfirm';
import {
  buildModelInstanceInferenceSpec,
  buildModelInstanceKvOffloadSpec,
  resolveModelInstanceNonPdInferenceTopology,
} from './inferencePayload';
import { sanitizeModelSource, serializeModelForApi } from './modelSource';
import { getModelInstanceSubmitError } from './submitValidate';

export { getModelSourceType, sanitizeModelSource, serializeModelForApi } from './modelSource';
export { getModelSizeStorageMismatchErrorKey, validateMaasStorage } from './submitValidate';

/** 模型实例提交组装选项；训练集群复用时不要传 elevateSharedEnvAndService */
export interface ModelInstancePayloadOptions {
  /** 为 true 时外层 commonEnv 只写 environment.env，不合并进各组 system.env */
  elevateSharedEnvAndService?: boolean;
  /** modelInstanceApi：节点写入 spec.inference；flatGroups：保留顶层 head/worker（训练集群） */
  layout?: 'modelInstanceApi' | 'flatGroups';
}

/** 顶层 initCommand：有有效脚本内容即写入 spec；SKU 模块裁剪由 filterMiSpecByEnabledModules 负责 */
function resolveGlobalInitCommand(values: ModelInstanceFormValues) {
  const raw = readInitCommandFieldsFromFormValues(values);
  if (!raw) return undefined;
  return initCommandFormFieldsToApi(raw);
}

function readInitCommandFieldsFromFormValues(values: ModelInstanceFormValues) {
  const raw = values.initCommand;
  if (!raw || typeof raw !== 'object') return undefined;
  return {
    _shellType: (raw as { _shellType?: 'bash' | 'sh' })._shellType,
    _commandsText: (raw as { _commandsText?: string })._commandsText,
    _argsText: (raw as { _argsText?: string })._argsText,
  };
}

function resolveTaskTemplate(values: ModelInstanceFormValues) {
  const steps = parseTaskTemplateField(values.taskTemplate);
  return steps.length ? steps : undefined;
}

/** 顶层 network.service：仅读取表单 `_inferenceService`，并注入入口节点 groupNames。 */
function resolveTopLevelNetworkService(values: ModelInstanceFormValues) {
  const raw = values._inferenceService;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const list = parseFormJsonField(raw);
  if (!Array.isArray(list) || !list.length) return undefined;
  const serialized = serializeServiceListForApi(list as Parameters<typeof serializeServiceListForApi>[0]);
  if (!serialized?.length) return undefined;
  const mountGroupNames = normalizeServiceMountGroupNames((values as Record<string, unknown>)[SERVICE_MOUNT_GROUP_NAME_FIELD]);
  if (!mountGroupNames.length) return serialized;
  return serialized.map((item) => ({ ...item, groupNames: mountGroupNames }));
}

/** 表单 → ModelInstanceSpec（业务逻辑保持不变） */
export function formToPayload(values: ModelInstanceFormValues, options?: ModelInstancePayloadOptions): ModelInstanceSpec {
  const layout = options?.layout ?? 'modelInstanceApi';
  const runtimeType = values.runtime?.type;
  const runMode = values.runMode || MaasRunMode.Normal;
  let headGroupSpec: ModelInstanceSpec['headGroupSpec'];
  let workerGroupSpecs: ModelInstanceSpec['workerGroupSpecs'];
  let inference: ModelInstanceSpec['inference'];
  let kvOffload: ModelInstanceSpec['kvOffload'];

  if (layout === 'modelInstanceApi') {
    const built = buildModelInstanceInferenceSpec(values, {
      elevateSharedEnvAndService: options?.elevateSharedEnvAndService === true,
      embeddedNodeConfig: true,
    });
    inference = built.inference;
    kvOffload = buildModelInstanceKvOffloadSpec(values, {
      elevateSharedEnvAndService: options?.elevateSharedEnvAndService === true,
      embeddedNodeConfig: true,
    });
  } else {
    const assembled = ensureFrameworkForSubmit(
      assembleGroupSpecsFromNodeFields(cloneDeep(values), values as Record<string, unknown>, {
        embeddedNodeConfig: true,
        elevateSharedEnvAndService: options?.elevateSharedEnvAndService === true,
      }),
    );
    const submitValues = mergeImageStorageVolumesIntoSubmitValues(attachPrimaryDataVolumeForSubmit(assembled), values);
    const mountPath = values.model?.mountPath;

    const resolvedGroups = resolveGroupSpecsForSubmit(submitValues, runtimeType, mountPath, {
      dataVolumeName: MODEL_DATA_VOLUME_NAME,
      embeddedNodeConfig: true,
    });
    const formRegistry = readRegistryPayloadFromValues(values as Record<string, unknown>);
    const groupRegistry =
      readGroupSystemRegistry(resolvedGroups.headGroupSpec) ?? readGroupSystemRegistry(resolvedGroups.workerGroupSpecs?.[0]);
    const registry = mergeRegistryPayload(formRegistry, groupRegistry);
    headGroupSpec = resolvedGroups.headGroupSpec ? placeRegistryOnGroupSystem(resolvedGroups.headGroupSpec, registry) : undefined;
    workerGroupSpecs = applyRegistryToGroups(resolvedGroups.workerGroupSpecs, registry);
    stripContainerDeployConfigFromGroupedSpec({ headGroupSpec, workerGroupSpecs });
  }

  const mountPath = values.model?.mountPath;
  const primaryTopology =
    layout === 'modelInstanceApi'
      ? resolveModelInstancePdSeparation(values)
        ? inference?.prefillDecode?.prefill
        : resolveModelInstanceNonPdInferenceTopology(inference)
      : { headGroupSpec, workerGroupSpecs };
  const formRegistry = readRegistryPayloadFromValues(values as Record<string, unknown>);
  const groupRegistry =
    readGroupSystemRegistry(primaryTopology?.headGroupSpec) ?? readGroupSystemRegistry(primaryTopology?.workerGroupSpecs?.[0]);
  const registry = mergeRegistryPayload(formRegistry, groupRegistry);

  const containerDeployConfig = resolveContainerDeployConfigFromFormValues(values);
  let gateway: ModelInstanceSpec['gateway'];
  if (isMiGatewayModuleEnabled(values)) {
    const gatewayNode = parseNodesField(values[GATEWAY_NODES_FIELD] ?? values.gatewayNodes)[0] as EmbeddedNodeSpec | undefined;
    if (gatewayNode) {
      const assembled = mergeEmbeddedNodeForSubmit(gatewayNode, {
        defaultName: 'gateway',
        elevateSharedEnvAndService: options?.elevateSharedEnvAndService === true,
      });
      let next = normalizeMaasGroupSpec(assembled, MaasGroupRoleType.Worker, runtimeType);
      if (next.resource) {
        next.resource = normalizeMaasGroupResourceForApi(next.resource) || next.resource;
      }
      next = placeRegistryOnGroupSystem(next, mergeRegistryPayload(registry, readGroupSystemRegistry(next)));
      gateway = next;
    }
  }
  if (layout === 'flatGroups') {
    stripContainerDeployConfigFromGroupedSpec({ headGroupSpec, workerGroupSpecs, gateway });
  } else {
    stripContainerDeployConfigFromGroupedSpec({ gateway });
  }

  const apiRunMode =
    layout === 'modelInstanceApi'
      ? runMode
      : resolveApiRunModeForSubmit({
          runMode,
          headGroupSpec,
          workerGroupSpecs,
        });

  const primarySystem = getPrimaryGroupFromValues(
    {
      headGroupSpec: primaryTopology?.headGroupSpec,
      workerGroupSpecs: primaryTopology?.workerGroupSpecs,
      runMode: apiRunMode,
    },
    apiRunMode,
  )?.system as Record<string, unknown> | undefined;

  const dataVolume = resolvePrimaryDataVolumeFromValues(values);
  const useExistingPvc = !!dataVolume && isExistingDataVolume(dataVolume);
  const modelTypes = normalizeModelCategory(values._modelCategory ?? values.model?.types);
  const rawSources = values.model?.sources ?? [];
  const resolvedSources = rawSources.map((entry) => {
    const sourceRecord = entry as MaasModelSourceEntry & Record<string, unknown>;
    const core = sanitizeModelSource(sourceRecord as Record<string, unknown>);
    if (useExistingPvc && !core) {
      return { ...entry, ...buildExistingPvcModelSource(entry.name) };
    }
    return entry;
  });

  const model = serializeModelForApi({
    mountPath: mountPath || values.model?.mountPath || '/models',
    sources: resolvedSources,
    ...(modelTypes.length ? { types: modelTypes } : {}),
  }) as unknown as MaasModel | undefined;

  const commonEnv =
    layout === 'flatGroups'
      ? (resolveCommonEnvFromValues(values) ?? [])
          .filter((item) => item?.name)
          .map((item) => ({ name: item.name, value: item.value ?? '' }))
      : [];
  const globalInitCommand = layout === 'flatGroups' ? resolveGlobalInitCommand(values) : undefined;
  const taskTemplate = resolveTaskTemplate(values);

  const payload: ModelInstanceSpec = {
    alias: values.alias,
    description: values.description,
    workspace: values.workspace,
    creator: values.creator || getUserInfo()?.name || '',
    mode: values.mode || MaasMode.Provisioned,
    framework: values.framework || resolveFrameworkFromInferenceImage(null, primarySystem),
    ...(layout === 'flatGroups' ? { runMode: apiRunMode } : {}),
    runtime: normalizeMaasRuntime(values.runtime) || values.runtime,
    network: {
      ...(values.network?.vpc ? { vpc: values.network.vpc } : {}),
      ...(values.network?.securitygroup ? { securitygroup: values.network.securitygroup } : {}),
    },
    ...(layout === 'flatGroups' && commonEnv.length ? { environment: { env: commonEnv } } : {}),
    ...(layout === 'flatGroups' && typeof values.replicas === 'number' && values.replicas >= 0 ? { replicas: values.replicas } : {}),
    ...(layout === 'flatGroups' ? { headGroupSpec, workerGroupSpecs } : {}),
    ...(layout === 'modelInstanceApi' && inference ? { inference } : {}),
    ...(layout === 'modelInstanceApi' && kvOffload ? { kvOffload } : {}),
    ...(gateway ? { gateway } : {}),
    model,
    ...(layout === 'flatGroups' && globalInitCommand ? { initCommand: globalInitCommand } : {}),
    ...(layout === 'flatGroups' && taskTemplate ? { taskTemplate } : {}),
  };

  // primaryDataVolume：询价 form→spec→toForm 往返载体（无节点组时 Data 卷无法挂到 group）；API 提交由 stripBillingOnly 剥离
  const maasFormAnnotations = buildModelInstanceMaasFormAnnotations(containerDeployConfig, {
    primaryDataVolume: values._primaryDataVolume,
    masterSystemDiskPricing: values.masterSystemDiskPricing,
    workerSystemDiskPricing: values.workerSystemDiskPricing,
  });
  if (maasFormAnnotations) {
    payload.annotations = maasFormAnnotations;
  }

  const networkService = resolveTopLevelNetworkService(values);
  if (networkService?.length) {
    payload.network = {
      ...(payload.network || {}),
      service: networkService,
    };
  }

  if (!payload.network?.vpc && !payload.network?.securitygroup && !payload.network?.service?.length) {
    delete payload.network;
  }

  return payload;
}

/** 仅从表单读取并组装 spec，不请求 resource-configurations（订单 metadata 等场景） */
export function readModelInstanceSpecFromForm(
  form: FormInstance,
  options?: { overlay?: Partial<ModelInstanceFormValues> } & ModelInstancePayloadOptions,
): { values: ModelInstanceFormValues; spec: ModelInstanceSpec } {
  const values = {
    ...readModelInstanceFormValues(form),
    ...options?.overlay,
  };
  const payloadOptions: ModelInstancePayloadOptions = {
    elevateSharedEnvAndService: options?.elevateSharedEnvAndService,
    layout: options?.layout,
  };
  return { values, spec: formToPayload(values, payloadOptions) };
}

/** 读取表单、组装 spec、执行业务校验（提交入口单次调用） */
export async function prepareModelInstanceSubmit(
  form: FormInstance,
  options?: {
    overlay?: Partial<ModelInstanceFormValues>;
    initValues?: ModelInstanceFormValues;
    /** 复用节点组表单但不使用模型存储的业务页面跳过该段校验。 */
    skipModelStorageValidation?: boolean;
    /** 复用基础信息但后端只接收 runtime.type 的业务页面使用。 */
    runtimeTypeOnly?: boolean;
    /** 复用节点组表单但不配置服务的业务页面（如训练集群）跳过服务校验。 */
    skipServiceValidation?: boolean;
    /** 训练集群等复用提交编排但不走 docker-probe 的页面跳过。 */
    skipImageRegistryProbe?: boolean;
    /** 模型实例直建：外层环境变量只写顶层 environment.env */
    elevateSharedEnvAndService?: boolean;
    /** 训练集群直建：节点写入顶层 head/worker，而非 spec.inference */
    layout?: ModelInstancePayloadOptions['layout'];
    t?: (key: string, params?: Record<string, unknown>) => string;
  },
): Promise<{
  values: ModelInstanceFormValues;
  spec: ModelInstanceSpec;
  submitError?: MaasSubmitError;
}> {
  const payloadOptions: ModelInstancePayloadOptions = {
    elevateSharedEnvAndService: options?.elevateSharedEnvAndService,
    layout: options?.layout,
  };
  if (options?.t && !options.skipImageRegistryProbe) {
    const confirmed = await confirmModelInstanceImageOnForm(form, options.t);
    if (confirmed.ok === false) {
      const values = {
        ...readModelInstanceFormValues(form),
        ...options?.overlay,
      };
      return { values, spec: formToPayload(values, payloadOptions), submitError: { key: confirmed.message, alreadyNotified: true } };
    }
  }
  const values = {
    ...readModelInstanceFormValues(form),
    ...options?.overlay,
  };
  const spec = formToPayload(values, payloadOptions);
  const submitError = await getModelInstanceSubmitError(values, spec, {
    initValues: options?.initValues,
    skipModelStorageValidation: options?.skipModelStorageValidation,
    runtimeTypeOnly: options?.runtimeTypeOnly,
    skipServiceValidation: options?.skipServiceValidation,
  });
  return { values, spec, submitError };
}
